import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, Send } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

export default function Contact() {
    usePageTitle("Contact", "Call or text 516-730-5656 or email submissions@lended.com. Real people, real answers, one-business-hour response.");
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            setDone(true);
            toast.success("Message sent. We'll be in touch.");
        } catch {
            toast.error("Could not send — please try again.");
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "w-full h-12 px-4 bg-white border-2 border-black/10 focus:border-[#FF6B1A] outline-none rounded-none text-[15px] transition-colors";

    return (
        <>
            <section className="bg-white relative overflow-hidden" data-testid="contact-hero">
                <div className="absolute inset-0 bg-grid pointer-events-none" />
                <div className="container-lended relative pt-20 pb-12 md:pt-28 max-w-3xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="divider-orange" />
                        <span className="eyebrow">Contact</span>
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
                        We pick up. <span className="text-[#FF6B1A]">Real people. Real answers.</span>
                    </h1>
                    <p className="mt-7 text-lg text-black/70">
                        Got a deal? Got a question? Pick up the phone or send us a note. We respond within one business hour.
                    </p>
                </div>
            </section>

            <section className="bg-white py-16" data-testid="contact-grid">
                <div className="container-lended grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5 space-y-8">
                        <div className="border-l-4 border-[#FF6B1A] pl-5">
                            <Phone className="text-[#FF6B1A]" size={22} />
                            <div className="eyebrow mt-3">Call or Text</div>
                            <a href="tel:5167305656" className="font-display text-3xl md:text-4xl font-bold mt-1 block hover:text-[#FF6B1A]" data-testid="contact-phone">
                                516-730-5656
                            </a>
                            <p className="text-sm text-black/55 mt-2">Mon–Fri · 8am – 7pm ET</p>
                        </div>
                        <div className="border-l-4 border-[#FF6B1A] pl-5">
                            <Mail className="text-[#FF6B1A]" size={22} />
                            <div className="eyebrow mt-3">Email</div>
                            <a href="mailto:submissions@lended.com" className="font-display text-2xl md:text-3xl font-bold mt-1 block hover:text-[#FF6B1A]" data-testid="contact-email">
                                submissions@lended.com
                            </a>
                        </div>
                        <div className="border-l-4 border-[#FF6B1A] pl-5">
                            <MapPin className="text-[#FF6B1A]" size={22} />
                            <div className="eyebrow mt-3">Office</div>
                            <div className="font-display text-2xl font-bold mt-1">
                                New York, NY
                            </div>
                            <p className="text-sm text-black/55 mt-2">Direct funder serving all 50 states</p>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {done ? (
                            <div className="bg-white border-2 border-[#0A0A0A] p-10 text-center" data-testid="contact-success">
                                <div className="font-display text-3xl font-bold">Message received.</div>
                                <p className="text-black/65 mt-3">We'll respond within one business hour.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="bg-[#F8F5F2] border-2 border-[#0A0A0A] p-8 md:p-10" data-testid="contact-form">
                                <div className="grid md:grid-cols-2 gap-3">
                                    <input required placeholder="Name" value={form.name} onChange={onChange("name")} className={inputClass} data-testid="contact-name" />
                                    <input required type="email" placeholder="Email" value={form.email} onChange={onChange("email")} className={inputClass} data-testid="contact-email-input" />
                                    <input placeholder="Phone (optional)" value={form.phone} onChange={onChange("phone")} className={inputClass} data-testid="contact-phone-input" />
                                    <select value={form.subject} onChange={onChange("subject")} className={inputClass} data-testid="contact-subject">
                                        <option>General Inquiry</option>
                                        <option>I have a deal to submit</option>
                                        <option>ISO Partnership</option>
                                        <option>Press / Media</option>
                                    </select>
                                </div>
                                <textarea required rows={6} placeholder="How can we help?" value={form.message} onChange={onChange("message")} className={`${inputClass} h-auto py-3 mt-3`} data-testid="contact-message" />
                                <button type="submit" disabled={loading} className="btn-orange w-full justify-center mt-5 disabled:opacity-60" data-testid="contact-submit">
                                    {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                                    {loading ? "Sending..." : "Send Message"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
